/**
 * Subscription tier state (ROADMAP Epic 0). Separate axis from creditStore
 * (chat metering): tiers govern how much we do *for* the user, credits govern
 * how much they can chat.
 *
 * `plan` is the EFFECTIVE tier — the higher of `users/{uid}.plan` (webhook-
 * owned) and an active `grantedPlan` comp, mirroring the server's
 * `effective_plan`. AuthContext pushes both via `setEntitlements` on its
 * user-doc snapshot; the server-side entitlement guard (repositories/entitlements.py)
 * is the real authority — this store only drives UI (which CTA/gated surface
 * to show).
 */
import { create } from 'zustand';

export type Plan = 'free' | 'signals' | 'strategist';

const RANK: Record<Plan, number> = { free: 0, signals: 1, strategist: 2 };

/**
 * Coerce a raw `users/{uid}.plan` value (any legacy/unknown string, or a
 * missing field) into a valid Plan, failing safe to `free`. Used by
 * AuthContext when mirroring the provisioned entitlement into this store.
 */
export function normalizePlan(raw: unknown): Plan {
  const p = typeof raw === 'string' ? raw.toLowerCase() : '';
  return p === 'signals' || p === 'strategist' ? p : 'free';
}

/** True when `current` is at least `required` (rank-inclusive). Drives the
 *  plan-gating of paid AI modes in ModelSelector (mirrors the server-side
 *  has_entitlement check — the server stays authoritative). */
export function planMeets(current: Plan, required: Plan): boolean {
  return RANK[current] >= RANK[required];
}

/**
 * What a plan unlocks, framed by AI *mode access* — the honest, legible tier
 * differentiator. We deliberately do NOT surface a daily "questions" count:
 * questions vary in difficulty, so a number is meaningless to the user; the
 * real per-tier limit is an invisible usage backstop (the cost governor).
 * Free → Smart · Signals → +Pro · Strategist → +Expert.
 */
export function modesForPlan(plan: Plan): string {
  switch (plan) {
    case 'strategist':
      return 'Smart, Pro & Expert modes';
    case 'signals':
      return 'Smart & Pro modes';
    case 'free':
    default:
      return 'Smart mode';
  }
}

/** An active comp, as AuthContext reads it off `users/{uid}.grantedPlan`. */
export interface GrantView {
  plan: Plan;
  /** Epoch ms. An expired grant must read as no grant — expiry is enforced
   *  here exactly as `_entitlements.granted_plan` enforces it server-side. */
  expiresAt: number | null;
}

/** A trial code's trial (ROADMAP 0.R R6), running or ended. The access itself
 *  is the grant above; this is what lets the page say "your trial ended". */
export interface TrialView {
  plan: Plan;
  endsAt: number;
  ended: boolean;
}

export interface SubscriptionState {
  /** What is in force: the higher of paid and granted ([[BUGS #137]]). Every
   *  gate and label reads this — the same reconciliation `effective_plan`
   *  does server-side, after [[#59]] proved reading the raw field alone
   *  silently drops comped users. */
  plan: Plan;
  /** What they pay for (`users/{uid}.plan`, webhook-owned). The portal CTA
   *  keys off this: a grant has no Stripe subscription behind it. */
  paidPlan: Plan;
  /** The comp overlay, when one is active. */
  grant: GrantView | null;
  /** The account's trial, when it has had one. */
  trial: TrialView | null;
  /** How the paid plan is billed (ROADMAP 0.R R7); null when nothing is paid. */
  billingInterval: 'month' | 'year' | null;
  /** A cancellation is pending — access runs until `periodEnd`, then stops
   *  (ROADMAP 0.9). Mirrored off `users/{uid}.subscription`, webhook-owned. */
  cancelAtPeriodEnd: boolean;
  /** Epoch ms of the current paid period's end, when known. */
  periodEnd: number | null;
  /** A scheduled tier switch (ROADMAP 0.11): the plan this one becomes when
   *  the period turns. Mirrored off `users/{uid}.subscription.pendingPlan`. */
  pendingPlan: Plan | null;
  /** Whether entitlements have actually been RESOLVED for this session.
   *
   *  `plan: 'free'` is the initial value, so on its own it cannot be told apart
   *  from three different situations: nobody has looked yet, the user-doc read
   *  failed, and the user is genuinely on the free tier. A paywall that treats
   *  all three the same asks a paying subscriber to pay again ([[BUGS #147]]) —
   *  the most expensive mistake this store can cause.
   *
   *  True once a user-doc snapshot has landed (with or without a doc) or the
   *  session is known to be signed out. It stays FALSE when the read errored,
   *  so a surface that offers to charge can hold its tongue instead of
   *  guessing. The server gates the action either way. */
  entitlementsLoaded: boolean;
  /** AuthContext + the cancel/resume flows push the billing-cycle state. */
  setBillingCycle: (cancelAtPeriodEnd: boolean, periodEnd: number | null, pendingPlan?: Plan | null) => void;
  /** AuthContext pushes both sources; the effective plan is computed here. */
  setEntitlements: (paid: Plan, grant: GrantView | null) => void;
  /** entitlementService pushes the trial alongside the plan. */
  setTrial: (trial: TrialView | null) => void;
  setBillingInterval: (interval: 'month' | 'year' | null) => void;
  /** Paid-only path (sign-out reset, tests). Clears any grant. */
  setPlan: (plan: Plan) => void;
  /** True when the plan is at least Signals. */
  hasSignals: () => boolean;
  /** True when the plan is Strategist. */
  hasStrategist: () => boolean;
  /** True only when we KNOW the user is on the free tier — resolved, and free.
   *  This is the check a paid upsell should make, not `!hasSignals()`. */
  isKnownFree: () => boolean;
}

// An UNDATED grant confers nothing — the same call `_entitlements.granted_plan`
// makes server-side, and for the same reason: a comp that never expires because
// someone forgot a field is the expensive failure. Treating `expiresAt: null` as
// perpetual (as this did) put the client and the server on opposite sides of the
// one case they must agree on, in the worst direction — the UI offering a paid
// surface the server then refuses. It is reachable without an admin bug, too:
// AuthContext maps a non-Timestamp `expiresAt` (an ISO string written by hand or
// by a script) to `null`.
const activeGrant = (grant: GrantView | null): GrantView | null =>
  grant && grant.expiresAt !== null && grant.expiresAt > Date.now() && grant.plan !== 'free'
    ? grant
    : null;

const effective = (paid: Plan, grant: GrantView | null): Plan => {
  const g = activeGrant(grant);
  return g && RANK[g.plan] > RANK[paid] ? g.plan : paid;
};

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  plan: 'free',
  paidPlan: 'free',
  grant: null,
  trial: null,
  billingInterval: null,
  cancelAtPeriodEnd: false,
  periodEnd: null,
  pendingPlan: null,
  entitlementsLoaded: false,
  setBillingCycle: (cancelAtPeriodEnd, periodEnd, pendingPlan = null) =>
    set({ cancelAtPeriodEnd, periodEnd, pendingPlan }),
  // Both writers mark entitlements resolved: `setEntitlements` runs off a
  // user-doc snapshot, `setPlan` off a signed-out session or a test. Neither is
  // a guess. The unresolved state is the one nobody writes — the initial value,
  // and where a failed read leaves us.
  setEntitlements: (paid, grant) =>
    set({ paidPlan: paid, grant: activeGrant(grant), plan: effective(paid, grant), entitlementsLoaded: true }),
  setTrial: (trial) => set({ trial }),
  setBillingInterval: (billingInterval) => set({ billingInterval }),
  setPlan: (plan) => set({ plan, paidPlan: plan, grant: null, trial: null, billingInterval: null, cancelAtPeriodEnd: false, periodEnd: null, pendingPlan: null, entitlementsLoaded: true }),
  hasSignals: () => RANK[get().plan] >= RANK.signals,
  hasStrategist: () => RANK[get().plan] >= RANK.strategist,
  isKnownFree: () => get().entitlementsLoaded && get().plan === 'free',
}));
